import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart, Area,
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  ArrowRight, Lightbulb, FileText, Target, Clapperboard,
  Plug, Zap, TrendingUp, TrendingDown, Minus, Activity,
  Clock, CheckCircle2, Dna, Sparkles, User, Flame, Award,
  Palette, SlidersHorizontal, ArrowUpRight, Check,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState } from "@/components/states";
import { Button } from "@/components/ui/button";

/* ── palette ── */
const ACCENT   = "#E8522A";
const ACCENT2  = "#F2744A";
const MUTED    = "#E3DAD4";

// CSS-compatible color palette for charts
const CHART_COLORS = {
  amber:  "#F59E0B",
  green:  "#10B981",
  blue:   "#3B82F6",
  purple: "#8B5CF6",
  pink:   "#EC4899",
  rose:   "#F87171",
};

const STATUS_COLORS = {
  Draft: "#E3DAD4", Recording: "#93C5FD", Analyzing: "#FCD34D",
  Ready: "#6EE7B7", Published: "#E8522A",
};

const PIE_COLORS = [ACCENT, CHART_COLORS.amber, CHART_COLORS.green, CHART_COLORS.blue, CHART_COLORS.purple, CHART_COLORS.pink];

/* ── tiny helpers ── */
function StatCard({ icon: Icon, label, value, sub, color = ACCENT, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col gap-2.5 sm:gap-3 rounded-2xl border border-border bg-surface p-3.5 sm:p-5 shadow-sm text-left transition-all hover:border-accent/40 hover:shadow-md w-full"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl" style={{ background: color + "18" }}>
          <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5" style={{ color }} />
        </span>
        <span className="font-display text-2xl sm:text-3xl font-extrabold text-foreground tabular-nums">{value}</span>
      </div>
      <div>
        <p className="text-xs sm:text-sm font-semibold text-foreground truncate">{label}</p>
        {sub && <p className="mt-0.5 text-[11px] sm:text-xs text-muted-foreground truncate">{sub}</p>}
      </div>
    </button>
  );
}

function Card({ title, children, action, className = "" }) {
  return (
    <div className={`min-w-0 rounded-2xl border border-border bg-surface p-5 shadow-sm ${className}`}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{title}</p>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── custom tooltip ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-white px-3 py-2 shadow-lg text-xs">
      {label && <p className="mb-1 font-semibold text-foreground">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color || ACCENT }}>
          {p.name || p.dataKey}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

/* ── DNA dimension metadata ── */
const DNA_METADATA = {
  identity: { label: "Identity", icon: User },
  interests: { label: "Interests", icon: Flame },
  expertise: { label: "Expertise", icon: Award },
  experience: { label: "Experience", icon: Target },
  style: { label: "Style", icon: Palette },
  "content preferences": { label: "Preferences", icon: SlidersHorizontal },
  content_preferences: { label: "Preferences", icon: SlidersHorizontal },
  goals: { label: "Goals", icon: TrendingUp },
  constraints: { label: "Constraints", icon: Clock },
  niche: { label: "Niche", icon: Sparkles },
};

/* ── activity type badge ── */
const TYPE_ICON = { content: FileText, production: Clapperboard };
const STATUS_COLOR = {
  draft: "bg-elevated text-muted-foreground",
  recording: "bg-blue-50 text-blue-600",
  ready: "bg-green-50 text-green-700",
  published: "bg-accent/10 text-accent",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null });
  const [d, setD] = useState(null);
  const [rec, setRec]= useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [stats, recs] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/recommendations"),
      ]);
      setD(stats.data);
      setRec(recs.data);
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <AppShell title="Dashboard"><Loader label="Building your dashboard" /></AppShell>;
  if (state.error)   return <AppShell title="Dashboard"><ErrorState message={state.error} onRetry={load} /></AppShell>;

  const primary      = rec?.primary;
  const counts       = d.counts       || {};
  const dnaScore     = d.dna?.completeness?.score || 0;
  const dnaReady     = d.dna?.intelligence_ready || false;
  const dnaRadar     = d.dna_radar    || [];
  const oppScores    = d.opp_scores   || [];
  const statusChart  = d.status_chart || [];
  const pipeline     = d.pipeline_chart || [];
  const timelines    = d.content_timeline || [];
  const activity     = d.activity    || [];

  /* Build a continuous daily timeline over past 14-20 days so activity chart is smooth and never an isolated orphan dot */
  const dateMap = {};
  (d.content_timeline || []).forEach(({ date, count }) => {
    dateMap[date] = { content: count, ideas: 0 };
  });
  (d.ideas_timeline || []).forEach(({ date, count }) => {
    dateMap[date] = {
      content: dateMap[date]?.content || 0,
      ideas: count,
    };
  });

  const activeDates = Object.keys(dateMap).sort();
  const today = new Date();
  let refEndDate = new Date(today);
  if (activeDates.length > 0) {
    const latestActive = new Date(activeDates[activeDates.length - 1]);
    if (latestActive > refEndDate) refEndDate = latestActive;
  }

  let daysWindow = 14;
  if (activeDates.length > 0) {
    const oldestActive = new Date(activeDates[0]);
    const diff = Math.ceil((refEndDate - oldestActive) / (1000 * 60 * 60 * 24));
    if (diff > 14) daysWindow = Math.min(diff + 2, 30);
  }

  const combinedTimeline = [];
  for (let i = daysWindow - 1; i >= 0; i--) {
    const dt = new Date(refEndDate);
    dt.setDate(dt.getDate() - i);
    const key = dt.toISOString().slice(0, 10);
    const entry = dateMap[key];
    const month = dt.toLocaleDateString("en-US", { month: "short" });
    const day = dt.getDate();
    combinedTimeline.push({
      date: key,
      displayDate: `${month} ${day}`,
      content: entry ? entry.content : 0,
      ideas: entry ? entry.ideas : 0,
    });
  }

  return (
    <AppShell title="Dashboard">
      <div className="space-y-6">
        {/* ── Page Header ── */}
        <div className="flex flex-col gap-1 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Overview</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Ranked by your Creator DNA and real evidence.
            </p>
          </div>
        </div>

        {/* ── Recommended action banner ── */}
        {primary && (

          <section
            id="tour-next-action"
            className="relative overflow-hidden rounded-2xl p-5"
            style={{ background: `linear-gradient(135deg, #C94422 0%, ${ACCENT} 50%, ${ACCENT2} 100%)` }}
          >
            <div className="pointer-events-none absolute right-[-30px] top-[-30px] h-40 w-40 rounded-full opacity-20"
              style={{ background: "radial-gradient(circle, #FFD4A3, transparent 70%)" }} />
            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white/90">
                  <Zap className="h-3 w-3" /> Next action
                </div>
                <p className="font-display text-lg font-bold text-white">{primary.title}</p>
              </div>
              <Button onClick={() => navigate(primary.action_route)}
                className="shrink-0 rounded-full bg-white text-foreground shadow-lg hover:bg-white/90 hover:scale-105 transition-transform">
                {primary.action_label} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </section>
        )}

        {/* ── Stat cards ── */}
        <div id="tour-stats-cards" className="grid gap-2.5 sm:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 [&>*:last-child]:col-span-2 [&>*:last-child]:sm:col-span-1">
          <StatCard icon={Lightbulb}   label="Ideas"        value={counts.ideas       || 0} sub="generated"     color={CHART_COLORS.amber} onClick={() => navigate("/ideas")} />
          <StatCard icon={FileText}    label="Content"      value={counts.content     || 0} sub="drafts & ready" color={ACCENT}  onClick={() => navigate("/content")} />
          <StatCard icon={Target}      label="Opportunities" value={counts.opportunities || 0} sub="ranked"      color={CHART_COLORS.green} onClick={() => navigate("/opportunities")} />
          <StatCard icon={Clapperboard} label="Productions" value={counts.productions || 0} sub="in workspace"  color={CHART_COLORS.blue} onClick={() => navigate("/production")} />
          <StatCard icon={Plug}        label="Connections"  value={counts.connections || 0} sub="platforms live" color={CHART_COLORS.purple} onClick={() => navigate("/connections")} />
        </div>

        {/* ── Row 2: Creator DNA + Activity Timeline ── */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* ── Creator DNA Card (Clean, no AI slop) ── */}
          <div id="tour-creator-dna" className="rounded-2xl border border-border bg-surface p-5 shadow-sm min-w-0 flex flex-col justify-between">
            <div>
              {/* Header */}
              <div className="mb-4 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <Dna className="h-4 w-4" />
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Creator DNA
                  </h3>
                </div>

                {dnaReady ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-600 border border-emerald-500/20">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                    </span>
                    Intelligence Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-600 border border-amber-500/20">
                    Incomplete
                  </span>
                )}
              </div>

              {/* Clean Score Unit */}
              <div className="rounded-xl border border-border bg-elevated/40 p-3.5 mb-3 flex items-center justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-3xl font-black text-foreground">
                      {dnaScore}%
                    </span>
                    <span className="text-xs font-bold text-accent uppercase tracking-wider">
                      {dnaScore === 100 ? "Complete" : "In Progress"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {dnaRadar.slice(0, 6).filter((s) => s.filled).length} of 6 sections filled
                  </p>
                </div>

                {/* Compact Progress Ring */}
                <div className="relative h-12 w-12 shrink-0 flex items-center justify-center">
                  <svg width="48" height="48" viewBox="0 0 48 48">
                    <circle
                      cx="24"
                      cy="24"
                      r="19"
                      fill="none"
                      stroke="#E5E0DA"
                      strokeWidth="3.5"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="19"
                      fill="none"
                      stroke={ACCENT}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 19}`}
                      strokeDashoffset={`${2 * Math.PI * 19 * (1 - (dnaScore || 0) / 100)}`}
                      transform="rotate(-90 24 24)"
                      style={{ transition: "stroke-dashoffset 0.6s ease" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {dnaScore === 100 ? (
                      <Check className="h-4 w-4 text-accent stroke-[3]" />
                    ) : (
                      <span className="text-[10px] font-bold text-foreground">{dnaScore}%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 6 Dimension Pills Grid (Clean: No AI Slop subtitles) */}
              <div className="grid grid-cols-2 gap-2">
                {dnaRadar.slice(0, 6).map((s) => {
                  const key = (s.section || "").toLowerCase();
                  const meta = DNA_METADATA[key] || {
                    label: s.section,
                    icon: Sparkles,
                  };
                  const Icon = meta.icon;
                  return (
                    <button
                      key={s.section}
                      type="button"
                      onClick={() => navigate("/profile")}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2.5 transition-all text-left ${
                        s.filled
                          ? "border-border bg-white hover:border-accent/40 shadow-xs"
                          : "border-dashed border-border bg-elevated/20 hover:border-accent/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className={`h-4 w-4 shrink-0 ${s.filled ? "text-accent" : "text-muted-foreground"}`} />
                        <span className="truncate text-xs font-semibold text-foreground">
                          {meta.label}
                        </span>
                      </div>
                      <div className="ml-1 shrink-0">
                        {s.filled ? (
                          <Check className="h-3.5 w-3.5 text-emerald-600 stroke-[2.5]" />
                        ) : (
                          <span className="text-[10px] font-medium text-accent">+ Add</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Clean Footer */}
            <div className="mt-3.5 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {dnaScore === 100 ? "All sections configured" : "Complete profile to activate"}
              </span>
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="group inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline shrink-0"
              >
                <span>Edit Profile</span>
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* ── 30-Day Activity Side Component (Fixed UI) ── */}
          <div id="tour-activity-chart" className="rounded-2xl border border-border bg-surface p-5 shadow-sm min-w-0 flex flex-col justify-between md:col-span-2 lg:col-span-2">
            <div>
              {/* Header */}
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    30-Day Activity
                  </h3>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5 font-medium text-foreground">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.amber }} />
                    <span>Ideas: <strong className="font-semibold tabular-nums">{counts.ideas || 0}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 font-medium text-foreground">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ACCENT }} />
                    <span>Content: <strong className="font-semibold tabular-nums">{counts.content || 0}</strong></span>
                  </div>
                </div>
              </div>

              {/* Activity Chart Container */}
              <div className="h-56 min-w-0 pt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={combinedTimeline} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g-content" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={ACCENT} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g-ideas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={CHART_COLORS.amber} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={CHART_COLORS.amber} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={MUTED} vertical={false} opacity={0.6} />
                    <XAxis
                      dataKey="displayDate"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#8A827B" }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#8A827B" }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="content"
                      name="Content"
                      stroke={ACCENT}
                      strokeWidth={2}
                      fill="url(#g-content)"
                    />
                    <Area
                      type="monotone"
                      dataKey="ideas"
                      name="Ideas"
                      stroke={CHART_COLORS.amber}
                      strokeWidth={2}
                      fill="url(#g-ideas)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Footer Summary */}
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>Past {combinedTimeline.length} days</span>
              <span>{(counts.ideas || 0) + (counts.content || 0)} total creations</span>
            </div>
          </div>
        </div>

        {/* ── Row 3: Opportunity scores + Content status pie ── */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Opportunity bar chart - vertical with truncated names */}
          <Card title="Top Opportunities" className="md:col-span-2 lg:col-span-2"
            action={
              <Button variant="ghost" size="sm" onClick={() => navigate("/opportunities")}
                className="text-accent text-xs">
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            }>
            {oppScores.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                Generate opportunities to see scores here.
              </div>
            ) : (
              <div className="space-y-2.5">
                {oppScores.slice(0, 6).map((o, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {/* Rank */}
                    <span className="w-4 shrink-0 text-xs font-bold text-muted-foreground text-right">{i + 1}</span>
                    {/* Label */}
                    <span className="w-20 xs:w-28 sm:w-44 shrink-0 truncate text-xs font-medium text-foreground" title={o.name}>
                      {o.name}
                    </span>
                    {/* Bar */}
                    <div className="flex-1 h-2 overflow-hidden rounded-full bg-elevated">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${o.score}%`,
                          background: i === 0
                            ? ACCENT
                            : `linear-gradient(90deg, ${ACCENT2}, #FBAD9E)`,
                          opacity: 1 - i * 0.06,
                        }}
                      />
                    </div>
                    {/* Score */}
                    <span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums"
                      style={{ color: i === 0 ? ACCENT : "#756D68" }}>
                      {o.score}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Content status pie */}
          <Card title="Content by Status">
            {statusChart.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                No content yet.
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="h-36 w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusChart} cx="50%" cy="50%" innerRadius={40} outerRadius={64}
                        dataKey="value" nameKey="name" paddingAngle={3}>
                        {statusChart.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                  {statusChart.map((entry, i) => (
                    <div key={entry.name} className="flex items-center gap-1.5 text-[11px] text-foreground">
                      <span className="h-2 w-2 rounded-full shrink-0"
                        style={{ background: STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length] }} />
                      {entry.name} ({entry.value})
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* ── Row 4: Production pipeline + Recent activity ── */}
        <div className="grid gap-4 md:grid-cols-2">

          {/* Production pipeline bar */}
          <Card title="Production Pipeline">
            {pipeline.every((p) => p.count === 0) ? (
              <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
                No productions yet.
              </div>
            ) : (
              <div className="h-44 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipeline} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={MUTED} vertical={false} />
                    <XAxis dataKey="stage" fontSize={10} tickLine={false} axisLine={false} tick={{ fill: "#756D68" }} />
                    <YAxis fontSize={10} tickLine={false} axisLine={false} tick={{ fill: "#756D68" }} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" name="Projects" radius={[6, 6, 0, 0]}>
                      {pipeline.map((_, i) => (
                        <Cell key={i} fill={i === pipeline.length - 1 ? CHART_COLORS.green : ACCENT} fillOpacity={0.75 + i * 0.04} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Recent activity feed */}
          <Card title="Recent Activity">
            {activity.length === 0 ? (
              <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
                No activity yet.
              </div>
            ) : (
              <div className="space-y-2">
                {activity.map((item, i) => {
                  const Icon = TYPE_ICON[item.type] || Activity;
                  const sc = STATUS_COLOR[item.sub] || "bg-elevated text-muted-foreground";
                  const timeAgo = item.at ? (() => {
                    const diff = Date.now() - new Date(item.at).getTime();
                    const m = Math.round(diff / 60000);
                    if (m < 1) return "just now";
                    if (m < 60) return `${m}m ago`;
                    const h = Math.round(m / 60);
                    if (h < 24) return `${h}h ago`;
                    return `${Math.round(h / 24)}d ago`;
                  })() : "";
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5">
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-elevated">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <span className="flex-1 truncate text-sm font-medium text-foreground">{item.label}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${sc}`}>
                        {item.sub}
                      </span>
                      {timeAgo && (
                        <span className="shrink-0 flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <Clock className="h-3 w-3" /> {timeAgo}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

      </div>
    </AppShell>
  );
}
